import React from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Box,
  Text,
  Flex,
  Spinner,
  Link,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import StatusBadge from './StatusBadge';
import RefundDeadlineBadge from './RefundDeadlineBadge';
import {
  formatCaseId,
  formatCOP,
  formatRefundDate,
  getStudentDisplayName,
} from 'api/administrador/refunds';

const thStyles = {
  bg: 'white',
  color: 'gray.600',
  fontSize: 'xs',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  letterSpacing: 'wider',
  borderColor: 'gray.200',
  py: 3,
  px: 4,
  whiteSpace: 'nowrap',
};

const cellFontSize = 'sm';
const linkColor = '#574feb';
const programMaxW = '130px';

export default function RefundTable({ items, loading }) {
  if (loading) {
    return (
      <Flex justify="center" py={10}>
        <Spinner color="#574feb" />
      </Flex>
    );
  }

  if (!items?.length) {
    return (
      <Box py={10} textAlign="center" borderLeft="3px solid #574feb">
        <Text color="gray.500">Sin Resultados</Text>
      </Box>
    );
  }

  return (
    <TableContainer overflowX="auto" borderLeft="3px solid #574feb">
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th {...thStyles}># Caso</Th>
            <Th {...thStyles}>Estudiante</Th>
            <Th {...thStyles}>Código</Th>
            <Th {...thStyles} maxW={programMaxW}>
              Programa
            </Th>
            <Th {...thStyles}>Valor pagado</Th>
            <Th {...thStyles}>A devolver</Th>
            <Th {...thStyles}>Estado</Th>
            <Th {...thStyles}>Plazo</Th>
            <Th {...thStyles}>Fecha</Th>
            <Th {...thStyles} textAlign="center">
              Ver
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {items.map((item, index) => {
            const programName = item.program_type?.name || '—';

            return (
              <Tr
                key={item.id}
                bg={index % 2 === 0 ? 'white' : '#F8F9FD'}
                _hover={{ bg: index % 2 === 0 ? 'gray.50' : '#F0F2FA' }}
              >
                <Td py={4} px={4} borderColor="gray.100">
                  <Text fontWeight="bold" color="gray.800" fontSize={cellFontSize}>
                    {formatCaseId(item.id)}
                  </Text>
                </Td>
                <Td py={4} px={4} borderColor="gray.100">
                  <Link
                    as={RouterLink}
                    to={`/plataforma/administrador/ver-estudiante/${item.student?.id}`}
                    color={linkColor}
                    fontSize={cellFontSize}
                    fontWeight="medium"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {getStudentDisplayName(item.student)}
                  </Link>
                </Td>
                <Td py={4} px={4} color="gray.700" fontSize={cellFontSize} borderColor="gray.100">
                  {item.student?.id || '—'}
                </Td>
                <Td py={4} px={4} borderColor="gray.100" maxW={programMaxW}>
                  <Tooltip label={programName} hasArrow placement="top" isDisabled={programName === '—'}>
                    <Text
                      color="gray.700"
                      fontSize={cellFontSize}
                      noOfLines={1}
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {programName}
                    </Text>
                  </Tooltip>
                </Td>
                <Td py={4} px={4} color="gray.700" fontSize={cellFontSize} borderColor="gray.100">
                  {formatCOP(item.paid_amount)}
                </Td>
                <Td py={4} px={4} fontWeight="bold" color="gray.800" fontSize={cellFontSize} borderColor="gray.100">
                  {formatCOP(item.refund_amount)}
                </Td>
                <Td py={4} px={4} borderColor="gray.100">
                  <StatusBadge status={item.status} />
                </Td>
                <Td py={4} px={4} borderColor="gray.100" whiteSpace="nowrap">
                  <RefundDeadlineBadge refund={item} showDueDate compact />
                </Td>
                <Td py={4} px={4} color="gray.700" fontSize={cellFontSize} borderColor="gray.100" whiteSpace="nowrap">
                  {formatRefundDate(item.createdAt)}
                </Td>
                <Td py={4} px={4} borderColor="gray.100" textAlign="center">
                  <IconButton
                    as={RouterLink}
                    to={`/plataforma/administrador/devoluciones/${item.id}`}
                    aria-label="Ver"
                    icon={<FiArrowRight />}
                    size="sm"
                    minW="32px"
                    h="32px"
                    bg="#574feb"
                    color="white"
                    borderRadius="md"
                    _hover={{ bg: '#4840d4' }}
                  />
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
